import { cloneDeep } from 'lodash';
import {
    ContentType,
    ContentZoneItem,
    ContentZones,
    CustomContent,
    CustomWidgetContent,
    DateFacetRange,
    FacetType,
    FacetValue,
    FacetValueColor,
    FeaturedItemsContent,
    HawksearchGlobal,
    HawksearchState,
    ImageContent,
    ModifiedQueryData,
    NumericFacetRange,
    PopularQueriesContent,
    PopularQueriesContentItem,
    QuerySuggestionsData,
    RawFacet,
    RawFacetValue,
    RawFeaturedItem,
    RawMerchandisingItem,
    RawSearchRequest,
    RawSearchResponse,
    RawSearchResultsItem,
    RawSearchSelection,
    SearchRequest,
    SearchResponse,
    SearchResultsItem,
    SelectedFacet,
    SelectedFacets
} from '../models';
import { BaseService } from './base.service';
import { trackingService } from './tracking.service';

declare let Hawksearch: HawksearchGlobal;

export class SearchService extends BaseService {
    protected baseUrl = Hawksearch.config.endpoints?.search || 'https://searchapi-dev.hawksearch.net/api/v2';

    // #region Search Operations

    async search(searchRequest: SearchRequest): Promise<SearchResponse> {
        const searchReults = await this.executeSearch(searchRequest, true, false);

        this.bindComponents(searchReults);

        return searchReults;
    }

    async query(query?: string, disableSpellcheck = false): Promise<SearchResponse> {
        if (query) {
            const recentQueries = this.getRecentQueries();
            const recentQueryLimit = 5;

            if (!recentQueries.includes(query)) {
                if (recentQueries.length >= recentQueryLimit) {
                    recentQueries.splice(recentQueryLimit - 1);
                }

                recentQueries.unshift(query);

                this.saveRecentQueries(recentQueries);
            }
        }

        const request: SearchRequest = {
            disableSpellcheck: !!disableSpellcheck || undefined,
            query: query,
            newRequest: true
        };

        const searchResponse = await this.executeSearch(request, true, false);

        this.bindComponents(searchResponse);

        return searchResponse;
    }

    async addFacetValue(field: string, value: string): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const searchRequest = cloneDeep(Hawksearch.searchRequest!);

        searchRequest.newRequest = false;
        searchRequest.page = undefined;

        if (!searchRequest.facets) {
            searchRequest.facets = {};
        }

        this.clearExistingFacetValue(searchRequest, field, value);

        searchRequest.facets[field] = [...(searchRequest.facets[field]?.filter((v) => v !== value) ?? []), value];

        const searchResponse = await this.executeSearch(searchRequest, false);

        this.bindFacetsListComponent(searchResponse);
        this.bindPaginationComponent(searchResponse);
        this.bindSelectedFacetsComponent(searchResponse);
        this.bindTabsComponent(searchResponse);
        this.bindZoneComponent(searchResponse);

        return searchResponse;
    }

    async removeFacetValue(field: string, value: string): Promise<SearchResponse> {
        this.ensureSearchRequest();

        if (field === 'searchWithin') {
            return await this.searchWithin('');
        }

        const searchRequest = cloneDeep(Hawksearch.searchRequest!);

        searchRequest.newRequest = false;
        searchRequest.page = undefined;

        this.clearExistingFacetValue(searchRequest, field, value);

        const searchResponse = await this.executeSearch(searchRequest, false);

        this.bindFacetsListComponent(searchResponse);
        this.bindPaginationComponent(searchResponse);
        this.bindSelectedFacetsComponent(searchResponse);
        this.bindTabsComponent(searchResponse);
        this.bindZoneComponent(searchResponse);

        return searchResponse;
    }

    includeFacetValue(field: string, value: string): Promise<SearchResponse> {
        const excludedValue = `@${value}`;

        return this.removeFacetValue(field, excludedValue);
    }

    async excludeFacetValue(field: string, value: string): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const excludedValue = `@${value}`;
        const searchRequest = cloneDeep(Hawksearch.searchRequest!);

        searchRequest.newRequest = false;
        searchRequest.page = undefined;

        if (!searchRequest.facets) {
            searchRequest.facets = {};
        }

        this.clearExistingFacetValue(searchRequest, field, value);

        searchRequest.facets = {
            ...searchRequest.facets,
            [field]: [...(searchRequest.facets[field] ?? []), excludedValue]
        };

        const searchResponse = await this.executeSearch(searchRequest, false);

        this.bindFacetsListComponent(searchResponse);
        this.bindPaginationComponent(searchResponse);
        this.bindSelectedFacetsComponent(searchResponse);
        this.bindTabsComponent(searchResponse);
        this.bindZoneComponent(searchResponse);

        return searchResponse;
    }

    private clearExistingFacetValue(searchRequest: SearchRequest, field: string, value: string): void {
        value = value.replace(/^@/, '');

        const excludedValue = `@${value}`;

        if (!searchRequest.facets) {
            searchRequest.facets = {};
        }

        searchRequest.facets = {
            ...searchRequest.facets,
            [field]: [...(searchRequest.facets[field]?.filter((v) => v !== value && v !== excludedValue) ?? [])]
        };

        if (!searchRequest.facets[field]?.length) {
            delete searchRequest.facets[field];
        }
    }

    async setFacetValue(field: string, value: string): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const request = cloneDeep(Hawksearch.searchRequest!);

        request.newRequest = false;
        request.page = undefined;

        if (!request.facets) {
            request.facets = {};
        }

        request.facets[field] = [value];

        const searchResponse = await this.executeSearch(request, false);

        this.bindFacetsListComponent(searchResponse);
        this.bindPaginationComponent(searchResponse);
        this.bindSelectedFacetsComponent(searchResponse);
        this.bindTabsComponent(searchResponse);
        this.bindZoneComponent(searchResponse);

        return searchResponse;
    }

    async modifySearch(query: string | undefined, facets: SelectedFacets | undefined): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const request = cloneDeep(Hawksearch.searchRequest!);

        request.query = query;
        request.facets = facets;
        request.newRequest = false;
        request.page = undefined;

        const searchResponse = await this.executeSearch(request, false);

        this.bindFacetsListComponent(searchResponse);
        this.bindPaginationComponent(searchResponse);
        this.bindSelectedFacetsComponent(searchResponse);
        this.bindTabsComponent(searchResponse);
        this.bindZoneComponent(searchResponse);

        return searchResponse;
    }

    async searchWithin(value: string): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const request = cloneDeep(Hawksearch.searchRequest!);

        request.newRequest = false;
        request.page = undefined;
        request.searchWithin = value || undefined;

        const searchResponse = await this.executeSearch(request, false);

        this.bindFacetsListComponent(searchResponse);
        this.bindPaginationComponent(searchResponse);
        this.bindSelectedFacetsComponent(searchResponse);
        this.bindTabsComponent(searchResponse);
        this.bindZoneComponent(searchResponse);

        return searchResponse;
    }

    async selectTab(value?: string): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const request = cloneDeep(Hawksearch.searchRequest!);

        request.newRequest = false;
        request.page = undefined;

        if (!request.facets) {
            request.facets = {};
        }

        const field = Hawksearch.searchResponse?.facets?.find((f) => f.type === 'tabs')?.field;

        if (field) {
            if (value) {
                request.facets[field] = [value];
            } else {
                delete request.facets[field];
            }
        }

        const searchResponse = await this.executeSearch(request, false);

        this.bindFacetsListComponent(searchResponse);
        this.bindPaginationComponent(searchResponse);
        this.bindSelectedFacetsComponent(searchResponse);
        this.bindTabsComponent(searchResponse);
        this.bindZoneComponent(searchResponse);

        return searchResponse;
    }

    async paginate(page: number): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const request = cloneDeep(Hawksearch.searchRequest!);

        request.newRequest = false;
        request.page = page;

        const searchResponse = await this.executeSearch(request, false);

        this.bindPaginationComponent(searchResponse);

        return searchResponse;
    }

    async setPageSize(pageSize: number): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const request = cloneDeep(Hawksearch.searchRequest!);

        request.newRequest = false;
        request.pageSize = pageSize;

        const searchResponse = await this.executeSearch(request, false);

        this.bindPageSizeComponent(searchResponse);
        this.bindPaginationComponent(searchResponse);

        return searchResponse;
    }

    async sort(value?: string): Promise<SearchResponse> {
        this.ensureSearchRequest();

        const request = cloneDeep(Hawksearch.searchRequest!);

        request.newRequest = false;
        request.page = 1;
        request.sort = value;

        const searchResponse = await this.executeSearch(request, false);

        this.bindPaginationComponent(searchResponse);
        this.bindSortingComponent(searchResponse);

        return searchResponse;
    }

    private executeSearch(searchRequest: SearchRequest, newSearch: boolean, executeDefaultBindings = true): Promise<SearchResponse> {
        Hawksearch.searchRequest = searchRequest;

        const rawRequest: RawSearchRequest = {
            ClientData: {
                UserAgent: window.navigator.userAgent,
                VisitorId: this.getVisitorId(),
                VisitId: this.getVisitId()
            },
            ClientGuid: Hawksearch.config.clientId,
            IgnoreSpellcheck: searchRequest.disableSpellcheck,
            Keyword: searchRequest.query,
            FacetSelections: searchRequest.facets,
            MaxPerPage: searchRequest.pageSize,
            PageNo: searchRequest.page,
            SearchWithin: searchRequest.searchWithin,
            SortBy: searchRequest.sort
        };

        const event = new CustomEvent('hawksearch:before-search-executed', { detail: rawRequest });

        dispatchEvent(event);

        return this.httpPost<RawSearchResponse>('/search', rawRequest).then((rawSearchResponse) => {
            const searchResponse = this.convertSearchResponse(searchRequest, rawSearchResponse);

            Hawksearch.searchResponse = searchResponse;

            trackingService.trackSearch(searchRequest.query, newSearch);

            this.setQueryString();

            this.triggerEvent('hawksearch:search-completed', searchResponse);

            if (executeDefaultBindings) {
                this.bindModifiedQueryComponent(searchResponse);
                this.bindSearchComponent(searchResponse);
                this.bindSearchResultsListComponent(searchResponse);
                this.bindQuerySuggestionsComponent(searchResponse);
            }

            if (newSearch && searchResponse.redirect) {
                window.open(searchResponse.redirect.url, searchResponse.redirect.target ?? '_self');
            }

            return searchResponse;
        });
    }

    private ensureSearchRequest(): void {
        if (!Hawksearch.searchRequest) {
            throw new Error('You cannot refine search results until an initial search has been executed');
        }
    }

    // #endregion Search Operations

    // #region Model Binding

    private convertSearchResponse(searchRequest: SearchRequest, searchResponse: RawSearchResponse): SearchResponse {
        const getFacetType = (facet: RawFacet): FacetType => {
            if (facet.FieldType === 'tab') {
                return 'tabs';
            }

            switch (facet.FacetType.toLowerCase()) {
                case 'checkbox':
                case 'nestedcheckbox':
                    return 'checkbox';
                case 'color':
                    return 'color';
                case 'link':
                    return 'link';
                case 'openrange':
                    if (facet.DataType === 'datetime') {
                        return 'date-range';
                    } else if (facet.IsCurrency || facet.IsNumeric) {
                        return 'numeric-range';
                    }
                case 'recentsearches':
                    return 'recent-searches';
                case 'related':
                    return 'related-searches';
                case 'search':
                    return 'search';
                case 'size':
                    return 'size';
                case 'slider':
                    return 'range-slider';
                case 'swatch':
                    return 'color';
                default:
                    return 'unsupported';
            }
        };

        const getFacetRange = (facet: RawFacet): DateFacetRange | NumericFacetRange | undefined => {
            const facetType = getFacetType(facet);
            const rangeFacetTypes: Array<FacetType> = ['date-range', 'numeric-range', 'range-slider'];

            if (!rangeFacetTypes.includes(facetType)) {
                return undefined;
            }

            if (facet.DataType === 'datetime') {
                return {
                    type: 'date',
                    min: new Date(Date.parse(facet.Values![0].RangeMin)),
                    max: new Date(Date.parse(facet.Values![0].RangeMax)),
                    start: new Date(Date.parse(facet.Values![0].RangeStart)),
                    end: new Date(Date.parse(facet.Values![0].RangeEnd))
                };
            } else if (facet.IsCurrency || facet.IsNumeric) {
                return {
                    type: 'numeric',
                    min: parseFloat(facet.Values![0].RangeMin),
                    max: parseFloat(facet.Values![0].RangeMax),
                    start: parseFloat(facet.Values![0].RangeStart),
                    end: parseFloat(facet.Values![0].RangeEnd)
                };
            }

            return undefined;
        };

        const getFacetValueImage = (facet: RawFacet, value: RawFacetValue): string | undefined => {
            const rangeValue = facet.Ranges?.find((r) => r.Value.toLowerCase() === value.Value.toLowerCase());

            if (!rangeValue?.AssetFullUrl) {
                return undefined;
            }

            return this.getUrl(Hawksearch.config.urlPrefixes?.assets, rangeValue.AssetFullUrl);
        };

        const getFacetValueColor = (facet: RawFacet, value: RawFacetValue): FacetValueColor | undefined => {
            let swatchValue = facet.SwatchData?.find((v) => v.Value.toLowerCase() === value.Value.toLowerCase());

            if (!swatchValue) {
                swatchValue = facet.SwatchData?.find((v) => v.IsDefault);
            }

            if (!swatchValue) {
                return undefined;
            }

            return {
                name: value.Label,
                hex: swatchValue.Color || swatchValue.Value,
                imageUrl: this.getUrl(Hawksearch.config.urlPrefixes?.assets, swatchValue.AssetUrl || swatchValue.AssetName)
            };
        };

        const getFacetValues = (
            facet: RawFacet,
            values: Array<RawFacetValue> | undefined,
            selection: RawSearchSelection | undefined
        ): Array<FacetValue> | undefined => {
            const facetType = getFacetType(facet);

            if (facetType === 'recent-searches') {
                const recentQueries: Array<string> = this.getRecentQueries();

                return recentQueries.map((s) => ({
                    title: s
                }));
            }

            return values?.map((v) => ({
                title: v.Label,
                imageUrl: getFacetValueImage(facet, v),
                value: v.Value,
                color: getFacetValueColor(facet, v),
                count: v.Count,
                level: v.Level,
                selected: v.Selected,
                excluded: selection?.Items?.some((s) => s.Value === `@${v.Value}`),
                children: getFacetValues(facet, v.Children, selection)
            }));
        };

        const getFacetVisible = (facet: RawFacet): boolean => {
            const facetType = getFacetType(facet);

            if (facetType === 'recent-searches') {
                const values = getFacetValues(facet, undefined, undefined);

                return !!values?.length;
            }

            return facet.IsVisible ?? true;
        };

        const getSearchResultsItem = (item: RawSearchResultsItem): SearchResultsItem => ({
            id: item.DocId,
            pinned: item.IsPin ?? false,
            visible: item.IsVisible ?? true,
            score: item.Score,
            type: ['item', 'product'].includes(item.Document?.[this.fieldMappings.type]?.[0]?.toLowerCase()) ? 'product' : 'content',
            imageUrl: this.getUrl(Hawksearch.config.urlPrefixes?.content, this.getString(item.Document?.[this.fieldMappings.imageUrl])),
            title: this.getString(item.Document?.[this.fieldMappings.title])!,
            url: this.getUrl(Hawksearch.config.urlPrefixes?.content, this.getString(item.Document?.[this.fieldMappings.url]))!,
            price: this.getNumber(item.Document?.[this.fieldMappings.price]),
            salePrice: this.getNumber(item.Document?.[this.fieldMappings.salePrice]),
            attributes: item.Document
        });

        const getSelectedFacets = (): Array<SelectedFacet> => {
            return [
                ...Object.keys(searchResponse.Selections ?? []).map((field) => {
                    const selection = searchResponse.Selections![field];
                    const facet = searchResponse.Facets?.find((f) => (f.ParamName || f.Field) === field);
                    const excludedRegex = new RegExp('^@');

                    return {
                        field: field,
                        currency: facet?.IsCurrency ?? false,
                        title: selection.Label,
                        values: selection.Items.map((i) => ({
                            title: i.Label.replace(excludedRegex, ''),
                            value: i.Value,
                            excluded: excludedRegex.test(i.Value)
                        }))
                    };
                }),
                ...getSearchWithinSelectedFacets()
            ];
        };

        const getSearchWithinSelectedFacets = (): Array<SelectedFacet> => {
            const facetName = 'searchWithin';
            const facet = searchResponse.Facets?.find((f) => f.Field === facetName);

            if (!searchRequest.searchWithin || !facet) {
                return [];
            }

            return [
                {
                    field: facetName,
                    title: facet.Name,
                    currency: false,
                    values: [
                        {
                            title: searchRequest.searchWithin,
                            value: searchRequest.searchWithin,
                            excluded: false
                        }
                    ]
                }
            ];
        };

        const getContentItem = (rawItem: RawFeaturedItem | RawMerchandisingItem, prefix: '' | 'Mobile' | 'Tablet'): ContentType => {
            if ((prefix === 'Mobile' && !rawItem.IsMobile) || (prefix === 'Tablet' && !rawItem.IsTablet)) {
                prefix = '';
            }

            const contentItem: ContentType = {
                id: rawItem.BannerId,
                campaignId: rawItem.CampaignId,
                type: (rawItem as any)[prefix + 'ContentType'],
                title: rawItem.Title,
                zone: rawItem.Zone
            };

            switch (contentItem.type) {
                case 'custom': {
                    const custom = contentItem as CustomContent;

                    custom.content = (rawItem as any)[prefix + 'Output'];

                    break;
                }
                case 'featured': {
                    const featuredItems = contentItem as FeaturedItemsContent;

                    featuredItems.type = 'featured-items';
                    featuredItems.items = (rawItem as RawFeaturedItem).Items.map(getSearchResultsItem);

                    break;
                }
                case 'image': {
                    const image = contentItem as ImageContent;

                    image.image = {
                        url: this.getUrl(Hawksearch.config.urlPrefixes?.assets, (rawItem as any)[prefix + 'ImageUrl'])!,
                        height: (rawItem as any)[prefix + 'Height'] || undefined,
                        width: (rawItem as any)[prefix + 'Width'] || undefined,
                        altText: (rawItem as any)[prefix + 'AltTag'],
                        title: (rawItem as any)[prefix + 'AltTag']
                    };

                    image.link = (rawItem as any)[prefix + 'ForwardUrl']
                        ? {
                              url: (rawItem as any)[prefix + 'ForwardUrl'],
                              target: (rawItem as any)[prefix + 'Target'] || '_self'
                          }
                        : undefined;

                    break;
                }
                case 'widget': {
                    const widgetType = (rawItem as any)[prefix + 'WidgetType'];

                    switch (widgetType) {
                        case 'PopularSearchesWidget': {
                            const popularSearches = contentItem as PopularQueriesContent;

                            popularSearches.type = 'popular-queries';

                            const min = Math.min(...(rawItem as any)[prefix + 'Output'].map((k: any) => k.count));
                            const max = Math.max(...(rawItem as any)[prefix + 'Output'].map((k: any) => k.count));
                            const range = max - min;
                            const weights = 5;
                            const weightThresholds = new Array(weights).fill(0);

                            weightThresholds.forEach((v, i) => {
                                if (i === weights - 1) {
                                    weightThresholds[i] = 0;
                                } else {
                                    weightThresholds[i] = Math.floor(range / (i + 1)) + min;
                                }
                            });

                            popularSearches.items =
                                (rawItem as any)[prefix + 'Output'] instanceof Array
                                    ? (rawItem as any)[prefix + 'Output']
                                          .map(
                                              (k: any): PopularQueriesContentItem => ({
                                                  query: k.keyword,
                                                  count: k.count,
                                                  weight: weights - weightThresholds.findIndex((t) => k.count >= t)
                                              })
                                          )
                                          .sort((x: any, y: any) => x.query.localeCompare(y.query))
                                    : [];

                            break;
                        }
                        default: {
                            const customWidget = contentItem as CustomWidgetContent;

                            customWidget.type = 'custom-widget';
                            customWidget.data = rawItem.AltTag;
                        }
                    }
                }
            }

            return contentItem;
        };

        const getContentZones = (): ContentZones => {
            const zones: ContentZones = {};

            searchResponse.FeaturedItems?.Items.forEach((i) => {
                const zone = zones[i.Zone] ?? {
                    name: i.Zone
                };

                const item: ContentZoneItem = {
                    mobile: getContentItem(i, ''),
                    tablet: getContentItem(i, ''),
                    desktop: getContentItem(i, '')
                };

                zone.items = [...(zone.items ?? []), item];

                zones[i.Zone] = zone;
            });

            searchResponse.Merchandising?.Items.forEach((i) => {
                const zone = zones[i.Zone] ?? {
                    name: i.Zone
                };

                const item: ContentZoneItem = {
                    mobile: getContentItem(i, 'Mobile'),
                    tablet: getContentItem(i, 'Tablet'),
                    desktop: getContentItem(i, '')
                };

                zone.items = [...(zone.items ?? []), item];

                zones[i.Zone] = zone;
            });

            return zones;
        };

        return {
            contentZones: getContentZones(),
            facets: searchResponse.Facets?.map((f) => ({
                id: f.FacetId,
                type: getFacetType(f),
                title: f.Name,
                field: f.ParamName || f.Field,
                collapsible: f.IsCollapsible ?? false,
                collapsed: f.IsCollapsedDefault,
                range: getFacetRange(f),
                searchable: f.IsSearch,
                searchWithin: searchRequest.searchWithin,
                tooltip: this.stripHtml(f.Tooltip ?? '') || undefined,
                truncateThreshold: parseFloat(f.TruncateThreshold as any) || undefined,
                values: getFacetValues(f, f.Values, searchResponse.Selections?.[f.ParamName || f.Field]),
                visible: getFacetVisible(f)
            })).filter((f) => f.type !== 'unsupported'),
            modifiedQuery: searchResponse.AdjustedKeyword,
            pagination: searchResponse.Pagination
                ? {
                      page: searchResponse.Pagination.CurrentPage,
                      pageSize: searchResponse.Pagination.MaxPerPage,
                      totalPages: searchResponse.Pagination.NofPages,
                      totalResults: searchResponse.Pagination.NofResults,
                      maxPageLinks: searchResponse.Pagination.NumberOfPageLinks > 0 ? searchResponse.Pagination.NumberOfPageLinks : undefined,
                      displayFirstLink: searchResponse.Pagination.IsShowFirstLink,
                      displayLastLink: searchResponse.Pagination.IsShowLastLink,
                      options:
                          searchResponse.Pagination.Items.map((i) => ({
                              title: i.Label,
                              pageSize: i.PageSize,
                              selected: i.Selected,
                              default: i.Default
                          })) ?? []
                  }
                : undefined,
            query: searchResponse.Keyword,
            querySuggestions: searchResponse.DidYouMean,
            redirect: searchResponse.Redirect
                ? {
                      url: searchResponse.Redirect.Location,
                      target: searchResponse.Redirect.Target
                  }
                : undefined,
            results: searchResponse.Results?.map(getSearchResultsItem),
            selectedFacets: getSelectedFacets(),
            success: searchResponse.Success,
            sorting: {
                value: searchResponse.Sorting?.Value,
                options:
                    searchResponse.Sorting?.Items.map((i) => ({
                        title: i.Label,
                        value: i.Value,
                        selected: i.Selected,
                        default: i.IsDefault
                    })) ?? []
            },
            trackingId: searchResponse.TrackingId
        };
    }

    private getString(values: Array<string> | undefined, defaultValue?: string): string | undefined {
        return values?.[0] ?? defaultValue;
    }

    private getNumber(values: Array<string> | undefined, defaultValue?: number): number | undefined {
        const string = this.getString(values);
        let value = string ? parseFloat(string) : undefined;

        if (value !== undefined && isNaN(value)) {
            value = undefined;
        }

        return value ?? defaultValue;
    }

    private getUrl(prefix: string | undefined, url: string | undefined): string | undefined {
        if (!url) {
            return undefined;
        }

        if (!!prefix && !/^https?:\/\//.test(url)) {
            return `${prefix}/${url.replace(/^\/+/, '')}`;
        }

        return url;
    }

    // #endregion Model Binding

    // #region Events

    bindComponents(searchResponse: SearchResponse): void {
        this.bindSearchResultsComponent(searchResponse);

        setTimeout(() => {
            this.bindFacetsListComponent(searchResponse);
            this.bindModifiedQueryComponent(searchResponse);
            this.bindPageSizeComponent(searchResponse);
            this.bindPaginationComponent(searchResponse);
            this.bindQuerySuggestionsComponent(searchResponse);
            this.bindSearchComponent(searchResponse);
            this.bindSearchResultsListComponent(searchResponse);
            this.bindSelectedFacetsComponent(searchResponse);
            this.bindSortingComponent(searchResponse);
            this.bindTabsComponent(searchResponse);
            this.bindZoneComponent(searchResponse);
        });
    }

    bindFacetsListComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-facets-list', searchResponse?.facets);
    }

    bindModifiedQueryComponent(searchResponse: SearchResponse | undefined): void {
        const data: ModifiedQueryData = {
            display: !!searchResponse?.query && !!searchResponse?.modifiedQuery && Hawksearch.searchRequest!.newRequest,
            query: searchResponse?.query,
            modifiedQuery: searchResponse?.modifiedQuery
        };

        this.triggerEvent('hawksearch:bind-modified-query', data);
    }

    bindPageSizeComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-page-size', searchResponse?.pagination?.options);
    }

    bindPaginationComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-pagination', searchResponse?.pagination);
    }

    bindQuerySuggestionsComponent(searchResponse: SearchResponse | undefined): void {
        const data: QuerySuggestionsData = {
            display: !!searchResponse?.querySuggestions?.length && Hawksearch.searchRequest!.newRequest,
            querySuggestions: searchResponse?.querySuggestions
        };

        this.triggerEvent('hawksearch:bind-query-suggestions', data);
    }

    bindSortingComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-sorting', searchResponse?.sorting);
    }

    bindSearchComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-search', searchResponse);
    }

    bindSearchResultsComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-search-results', searchResponse);
    }

    bindSearchResultsListComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-search-results-list', searchResponse?.results);
    }

    bindSelectedFacetsComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-selected-facets', searchResponse?.selectedFacets);
    }

    bindTabsComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent(
            'hawksearch:bind-tabs',
            searchResponse?.facets?.find((f) => f.type === 'tabs')
        );
    }

    bindZoneComponent(searchResponse: SearchResponse | undefined): void {
        this.triggerEvent('hawksearch:bind-content-zone', searchResponse);
    }

    private triggerEvent<T>(name: string, data: T): void {
        const event = new CustomEvent(name, { detail: data });

        dispatchEvent(event);
    }

    // #endregion Events

    // #region QueryString

    getRequestFromUrl(): SearchRequest {
        const url = new URL(window.location.href);
        const facets: SelectedFacets = {};
        const facetRegex = new RegExp(`^${this.queryStringParams.facet}-`);

        for (const key of Array.from(url.searchParams.keys()).filter((k) => facetRegex.test(k))) {
            const field = key.replace(facetRegex, '');

            facets[field] = url.searchParams.getAll(key);
        }

        return {
            disableSpellcheck: this.getBooleanParameter(url.searchParams.get(this.queryStringParams.disableSpellcheck)),
            facets: facets,
            newRequest: true,
            pageSize: this.getPositiveIntegerParameter(url.searchParams.get(this.queryStringParams.pageSize)),
            page: this.getPositiveIntegerParameter(url.searchParams.get(this.queryStringParams.page)),
            query: this.getStringParameter(url.searchParams.get(this.queryStringParams.query)),
            searchWithin: this.getStringParameter(url.searchParams.get(this.queryStringParams.searchWithin)),
            sort: this.getStringParameter(url.searchParams.get(this.queryStringParams.sort))
        };
    }

    private setQueryString(): void {
        if (!Hawksearch.searchRequest) {
            return;
        }

        const request = Hawksearch.searchRequest;
        const url = new URL(window.location.href);

        url.searchParams.delete(this.queryStringParams.query);

        if (request.query) {
            url.searchParams.set(this.queryStringParams.query, request.query);
        }

        for (const key of url.searchParams.keys()) {
            if (key.startsWith(`${this.queryStringParams.facet}-`)) {
                url.searchParams.delete(key);
            }
        }

        if (request.disableSpellcheck) {
            url.searchParams.set(this.queryStringParams.disableSpellcheck, request.disableSpellcheck.toString());
        }

        if (request.facets) {
            Object.keys(request.facets).forEach((field) => {
                const values = request.facets![field];

                values?.forEach((value) => url.searchParams.append(`${this.queryStringParams.facet}-${field}`, value));
            });
        }

        url.searchParams.delete(this.queryStringParams.searchWithin);

        if (request.searchWithin) {
            url.searchParams.set(this.queryStringParams.searchWithin, request.searchWithin);
        }

        url.searchParams.delete(this.queryStringParams.sort);

        if (request.sort) {
            url.searchParams.set(this.queryStringParams.sort, request.sort);
        }

        url.searchParams.delete(this.queryStringParams.pageSize);

        if (request.pageSize) {
            url.searchParams.set(this.queryStringParams.pageSize, request.pageSize.toString());
        }

        url.searchParams.delete(this.queryStringParams.page);

        if (request.page) {
            url.searchParams.set(this.queryStringParams.page, request.page.toString());
        }

        const state: HawksearchState = {
            searchRequest: Hawksearch.searchRequest,
            searchResponse: Hawksearch.searchResponse!
        };

        if (url.toString() === window.location.href) {
            window.history.replaceState(state, '');
        } else {
            window.history.pushState(state, '', url.toString());
        }
    }

    private getBooleanParameter(input: string | null): boolean | undefined {
        if (input === null) {
            return undefined;
        }

        return input === 'true';
    }

    private getPositiveIntegerParameter(input: string | null): number | undefined {
        if (!input) {
            return undefined;
        }

        const value = parseInt(input);

        return value > 0 ? value : undefined;
    }

    private getStringParameter(input: string | null): string | undefined {
        if (!input || !input.trim()) {
            return undefined;
        }

        return input.trim();
    }

    // #endregion QueryString

    // #region Recent Queries

    private getRecentQueries(): Array<string> {
        const json = sessionStorage.getItem('recent-queries');

        return json ? JSON.parse(json) : [];
    }

    private saveRecentQueries(recentQueries: Array<string>): void {
        const json = JSON.stringify(recentQueries);

        sessionStorage.setItem('recent-queries', json);
    }

    // #endregion Recent Queries
}

export const searchService = new SearchService();
